/**
 * MediaLibraryModal - модальное окно медиа-библиотеки проекта
 * Позволяет выбрать существующее изображение или загрузить новое
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getProjectMedia, uploadMedia, deleteMedia, formatFileSize } from '../../../services/media';

export default function MediaLibraryModal({
  projectId,
  onSelect,
  onClose,
}) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  // Загрузка медиа при открытии
  useEffect(() => {
    loadMedia();
  }, [projectId]);

  const loadMedia = async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const mediaList = await getProjectMedia(projectId);
      setMedia(mediaList);
    } catch (err) {
      console.error('Error loading media:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка нового файла
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const newMedia = await uploadMedia(file, projectId, (progress) => {
        setUploadProgress(progress);
      });

      // Добавляем в начало списка
      setMedia((prev) => [newMedia, ...prev]);

      // Автоматически выбираем загруженное изображение
      setSelectedId(newMedia.id);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Удаление файла
  const handleDelete = async (item) => {
    if (!confirm('Удалить это изображение из библиотеки?')) return;

    setDeleting(item.id);

    try {
      await deleteMedia(projectId, item.id, item.storagePath);
      setMedia((prev) => prev.filter((m) => m.id !== item.id));

      // Сбрасываем выбор если удалили выбранное
      if (selectedId === item.id) {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  // Выбор изображения
  const handleSelect = () => {
    const selectedItem = media.find((m) => m.id === selectedId);
    if (selectedItem) {
      onSelect({
        src: selectedItem.src,
        storagePath: selectedItem.storagePath,
      });
    }
  };

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-background/95">
      <div className="bg-card rounded-2xl shadow-elevation-5 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Медиа-библиотека
            </h2>
            <p className="text-sm text-text-secondary">
              {media.length} {getFilesWord(media.length)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              iconPosition="left"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Загрузить
            </Button>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Progress bar */}
        {uploading && (
          <div className="px-4 py-2 bg-muted border-b border-border">
            <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
              <span>Загрузка...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-1.5 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-error/10 border-b border-error/20">
            <div className="flex items-center gap-2 text-error text-sm">
              <Icon name="AlertCircle" size={14} />
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto p-0.5 hover:bg-error/20 rounded"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center text-text-secondary">
                <Icon name="Loader2" size={32} className="mx-auto mb-2 animate-spin" />
                <p className="text-sm">Загрузка...</p>
              </div>
            </div>
          ) : media.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-text-secondary">
              <Icon name="Images" size={48} className="mb-3 opacity-50" />
              <p className="text-base font-medium mb-1">Библиотека пуста</p>
              <p className="text-sm">Загрузите первое изображение</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {media.map((item) => (
                <div
                  key={item.id}
                  className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                    selectedId === item.id
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <img
                    src={item.src}
                    alt={item.originalFilename || item.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />

                  {/* Selection indicator */}
                  {selectedId === item.id && (
                    <div className="absolute top-2 left-2 p-1 bg-primary rounded-full text-primary-foreground">
                      <Icon name="Check" size={14} />
                    </div>
                  )}

                  {/* Actions overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white truncate max-w-[120px]">
                          {item.originalFilename || item.filename}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item);
                          }}
                          disabled={deleting === item.id}
                          className="p-1 bg-error/80 hover:bg-error rounded text-white transition-colors disabled:opacity-50"
                          title="Удалить"
                        >
                          {deleting === item.id ? (
                            <Icon name="Loader2" size={14} className="animate-spin" />
                          ) : (
                            <Icon name="Trash2" size={14} />
                          )}
                        </button>
                      </div>
                      {item.size && (
                        <span className="text-xs text-white/70">
                          {formatFileSize(item.size)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Отмена
          </Button>
          <Button
            variant="default"
            iconName="Check"
            iconPosition="left"
            onClick={handleSelect}
            disabled={!selectedId}
          >
            Выбрать
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Хелпер для склонения слова "файл"
function getFilesWord(count) {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'файлов';
  }

  if (lastDigit === 1) {
    return 'файл';
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'файла';
  }

  return 'файлов';
}
